import { FORM_ITEM_TYPES } from '@sportninja/common/hooks/useCustomForm'
import { QuestionType } from '../constants/typeDisplayLabels'

/**
 * Single source of truth for translating between the builder's legacy question
 * type strings and the API's form item type ids.
 *
 * This replaces six hand-written switch statements (two module-level pairs in
 * TeamCustomForm / PlayerCustomForm plus two nested shadows inside
 * TeamCustomForm's add/update handlers). Each of those omitted a different
 * subset of types while QuestionTypeSelector exposed all of them in both
 * builders, so picking e.g. "Player Relationship" in the team builder silently
 * saved a Short Answer field instead.
 *
 * Typing the forward map as Record<QuestionType, number> makes it exhaustive:
 * adding a type to typeDisplayLabels without mapping it here is a compile
 * error rather than a silent fallback to Short Answer. The reverse map is
 * derived from the forward one, so the two directions cannot drift.
 */
/**
 * Hockey Canada (SN-8076) is deliberately absent from `typeDisplayLabels`, so
 * it is not a `QuestionType` and cannot live in the exhaustive record below.
 * The type is still fully supported everywhere else in the builder — existing
 * questions must round-trip rather than degrade to Short Answer — so it is
 * mapped separately here.
 */
const HOCKEY_CANADA_QUESTION_TYPE = 'hockey_canada'

export const QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID: Record<QuestionType, number> =
  {
    text: FORM_ITEM_TYPES.TEXT_FIELD,
    textarea: FORM_ITEM_TYPES.TEXT_AREA,
    number: FORM_ITEM_TYPES.NUMBER_FIELD,
    dropdown: FORM_ITEM_TYPES.DROPDOWN,
    multi_select: FORM_ITEM_TYPES.MULTI_SELECT_DROPDOWN,
    checkbox: FORM_ITEM_TYPES.CHECKBOX,
    date: FORM_ITEM_TYPES.DATE,
    birthdate: FORM_ITEM_TYPES.BIRTH_DATE,
    phone: FORM_ITEM_TYPES.PHONE,
    email: FORM_ITEM_TYPES.EMAIL,
    url: FORM_ITEM_TYPES.URL,
    country: FORM_ITEM_TYPES.COUNTRY,
    player_position: FORM_ITEM_TYPES.PLAYER_POSITION,
    hockey_usa: FORM_ITEM_TYPES.HOCKEY_USA,
    player_number: FORM_ITEM_TYPES.PLAYER_NUMBER,
    relationship: FORM_ITEM_TYPES.RELATIONSHIP,
    teamOfficialRole: FORM_ITEM_TYPES.TEAM_OFFICIAL_ROLE,
  }

const ALL_QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID: Record<string, number> = {
  ...QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID,
  [HOCKEY_CANADA_QUESTION_TYPE]: FORM_ITEM_TYPES.HOCKEY_CANADA,
}

const FORM_ITEM_TYPE_ID_TO_QUESTION_TYPE: Record<number, string> =
  Object.entries(ALL_QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID).reduce(
    (acc, [questionType, typeId]) => {
      acc[typeId] = questionType
      return acc
    },
    {} as Record<number, string>
  )

/**
 * Maps a builder question type to the API form item type id.
 * Unknown types fall back to Short Answer, matching the previous behaviour.
 */
export const getFormItemTypeId = (questionType: string): number =>
  ALL_QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID[questionType] ??
  FORM_ITEM_TYPES.TEXT_FIELD

/**
 * Maps an API form item type id back to the builder question type.
 * Unknown ids fall back to Short Answer, matching the previous behaviour.
 */
export const getQuestionType = (
  typeId: number
): QuestionType | typeof HOCKEY_CANADA_QUESTION_TYPE =>
  (FORM_ITEM_TYPE_ID_TO_QUESTION_TYPE[typeId] as
    | QuestionType
    | typeof HOCKEY_CANADA_QUESTION_TYPE) ?? 'text'
